import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { useInfiniteQuery } from 'react-query'

import { useClient } from '@hooks/useClient'
import { useDebounce } from '@hooks/useDebounce'
import { WriterOptionType } from '@models/user/WriterOptionType'
import { useOutside } from '@hooks/useOutside'
import {
  WithSearchWriterFieldProps,
  SearchWriterFieldProps,
  SearchActionEnum,
} from './interface'

const withSearchWriterField = (Component: React.FC<SearchWriterFieldProps>) => {
  function WithSearchWriterField({
    name,
    type,
    resetHandler,
    ...props
  }: WithSearchWriterFieldProps) {
    const client = useClient()
    const searchRef = useRef<HTMLDivElement>(null)
    const [isShowSearchLog, setIsShowSearchLog] = useState<boolean>(false)
    const [textSearch, setTextSearch] = useState('')
    const [text, setText] = useState('')
    const { submitForm } = useFormikContext()
    const [field, , { setValue }] = useField<number | undefined>(name)
    const { debounce } = useDebounce()

    const { fetchNextPage, data, isFetchingNextPage } = useInfiniteQuery(
      ['search-writer', textSearch],
      ({ pageParam = 1 }) =>
        client?.userClient.getWriterOption(textSearch, pageParam),
      {
        getNextPageParam: lastPage => {
          if (lastPage && lastPage.page * 20 < lastPage.total)
            return lastPage.page + 1

          return undefined
        },
      }
    )

    function handleFetchNextPage() {
      if (!isFetchingNextPage) fetchNextPage()
    }

    function showSearchLog() {
      setIsShowSearchLog(true)
    }

    function hideSearchLog() {
      setIsShowSearchLog(false)
    }

    function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
      setText(event.target.value)
    }

    function handleSelectWriter(writer: WriterOptionType) {
      setValue(type === SearchActionEnum.WRITER ? writer.id : writer.user.id)
      setText(writer.user.username)
      hideSearchLog()
      submitForm()
    }

    function removeTextSearch() {
      if (resetHandler) {
        resetHandler()
      }
      setValue(undefined)
      setText('')
    }

    useOutside(searchRef, () => {
      const prevText =
        data?.pages
          .flatMap(row => row?.data)
          .find(row => {
            return type === SearchActionEnum.WRITER
              ? row?.id === field.value
              : row?.user.id === field.value
          })?.user.username || ''
      hideSearchLog()
      setText(prevText)
    })

    const writers = useMemo(
      () => data?.pages.flatMap(page => page!.data) || [],
      [data]
    )

    useEffect(() => {
      if (!!!field.value) {
        setText('')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value])

    useEffect(() => {
      debounce(() => {
        setTextSearch(text)
      }, 400)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text])

    const componentsProps = {
      ...props,
      text,
      isShowSearchLog,
      value: field.value || '',
      writers,
      searchRef,
      showSearchLog,
      hideSearchLog,
      handleSelectWriter,
      handleTextChange,
      handleFetchNextPage,
      removeTextSearch,
    }
    return <Component {...componentsProps} />
  }

  return WithSearchWriterField
}

export { withSearchWriterField }
